import { Injectable, InjectionToken, Inject, Optional, Type, Injector } from '@angular/core';
import { SgAsyncGuiPlugin, SgAsyncGuiPlugins, SgAsyncGuiOptions, SgAsyncGuiPluginContext } from './types';
import { SgAsyncGuiRef } from './async-gui-ref';
/**
 * A provider token for default settings for SgAsyncGuiModule
 */
export const SG_ASYNC_GUI_DEFAULTS = new InjectionToken('SG_ASYNC_GUI_DEFAULTS');

/**
 * A Multi provider token for click confirm style plugins
 */
export const SG_ASYNC_GUI_PLUGIN = new InjectionToken('SG_ASYNC_GUI_PLUGIN');

@Injectable()
export class SgAsyncGuiPluginHost {
  private pluginStore = new Map<keyof SgAsyncGuiPlugins, SgAsyncGuiPlugin>();

  constructor(
    @Inject(SG_ASYNC_GUI_DEFAULTS) private defaultOptions: SgAsyncGuiOptions,
    @Optional() @Inject(SG_ASYNC_GUI_PLUGIN) private plugins: SgAsyncGuiPlugin[]
  ) {
    if (plugins) {
      for (let p of plugins) {
        this.register(p);
      }
    }
  }

  // plugin<P extends keyof SgAsyncGuiPlugins>(plugin: P): SgAsyncGuiPlugins[P] {
  plugin(plugin: keyof SgAsyncGuiPlugins): SgAsyncGuiPlugin {
    const p = this.pluginStore.get(plugin);
    if (!p) {
      throw new Error(`Invalid async gui style "${plugin}", did you regiter this plugin?`);
    }
    return p;
  }

  /**
   * Register the plugin.
   * This should not be used directly, use provider configuration intead.
   *
   * ```
   *   { provide: SG_CLICK_CONFIRM_PLUGIN, useClass: SgAsyncGuiPopoverPlugin, multi: true },
   * ```
   */
  register(plugin: SgAsyncGuiPlugin): void {
    this.pluginStore.set(plugin.name, plugin);
  }

  createContext(ctx: SgAsyncGuiPluginContext): SgAsyncGuiPluginContext {
    const options: SgAsyncGuiOptions = Object.assign({}, this.defaultOptions, ctx.options);
    if (!options.applyOn) {
      options.applyOn = ['run', 'resolve', 'reject'];
    }
    const component: Type<any> = options.component || this.defaultOptions.component; // if by accident was set to null/undefined etc...
    const newCtx: SgAsyncGuiPluginContext = Object.assign({}, ctx, { options, component });
    return newCtx;
  }
}

@Injectable()
export class SgAsyncGuiService {
  constructor(private injector: Injector, private pluginHost: SgAsyncGuiPluginHost) {}

  open(promise: Promise<any>, options: SgAsyncGuiOptions): SgAsyncGuiRef {
    const ctx: SgAsyncGuiPluginContext = {
      injector: this.injector,
      options: options,
      component: undefined, // pluginHost.createContext() will handle this
    };
    return this.pluginHost.plugin(options.style).open(promise, this.pluginHost.createContext(ctx));
  }
}
